import { NgService } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { DialogService } from "@app/services/dialog.service";
import { eAlertType, IPriceItem, IPriceItemBase, IPriceObject, IQuote, IRootScope, ISfdcCpqGetProductsRequest, ISfdcCpqGetProductsResponse, ISfdcCpqGetProductsResponse_Product2, ISfdcCpqPayload, ISfdcCpqPayloadOption } from "@models";
import { KPromise } from "@rules";
import { IMessage } from "@tools";

@NgService({
    token: Token.SfdcService,
    dependencies: [
        Token.$RootScope,
        Token.$Q,
        Token.DialogService,
    ]
})
export class SfdcService {
    private originalSfdcCpqPayload?: ISfdcCpqPayload;

    constructor(
        private $rootScope: IRootScope,
        private $q: ng.IQService,
        private dialogService: DialogService
    ) {

    }

    private cloneSfdcCpqPayload(payload: ISfdcCpqPayload): ISfdcCpqPayload {
        if (!payload) return payload;
        try {
            return JSON.parse(JSON.stringify(payload));
        } catch (e) {
            console.warn('Failed to clone SFDC CPQ payload', e);
            return payload;
        }
    }

    /**
     * Ensure the Sfdc Canvas SDK is available and restore canvas context from sessionStorage if needed.
     * Resolves when Sfdc.canvas.client is available; rejects if timeout or failure to load.
     */
    public ensureReady(timeoutMs: number = 3000): ng.IPromise<void> {
        const deferred = this.$q.defer<void>();

        // try to restore context from sessionStorage if missing
        try {
            if (!this.$rootScope.context.sfdcCanvasRequest) {
                const raw = sessionStorage.getItem("SfdcCanvasCookie");
                if (raw) {
                    try {
                        this.$rootScope.context.sfdcCanvasRequest = JSON.parse(raw);
                    } catch (e) {
                        console.warn('Failed to parse saved SfdcCanvasCookie', e);
                    }
                }
            }
        } catch (e) {
            console.warn('sessionStorage unavailable while restoring SfdcCanvasCookie', e);
        }

        // If the SDK client already exists resolve immediately
        if (window['Sfdc'] && window['Sfdc'].canvas && window['Sfdc'].canvas.client) {
            deferred.resolve();
            return deferred.promise;
        }

        // Try to inject the SDK script if it isn't loaded already
        try {
            const src = '/lib/canvas-all.js';
            if (!document.querySelector(`script[src="${src}"]`)) {
                const s = document.createElement('script');
                s.src = src;
                s.async = true;
                s.onload = () => {
                    // nothing here; we'll poll below for initialization
                };
                s.onerror = () => {
                    // can't load the SDK - still attempt a graceful reject after timeout
                    console.warn('Failed loading canvas SDK script', src);
                };
                document.head.appendChild(s);
            }
        } catch (e) {
            // ignore DOM access errors and continue to poll
            console.warn('Error injecting canvas SDK script', e);
        }

        // Poll for the Sfdc.client to become available
        const start = Date.now();
        const id = setInterval(() => {
            if (window['Sfdc'] && window['Sfdc'].canvas && window['Sfdc'].canvas.client) {
                clearInterval(id);
                deferred.resolve();
                return;
            }
            if (Date.now() - start > timeoutMs) {
                clearInterval(id);
                deferred.reject(new Error('Sfdc.canvas.client not available'));
            }
        }, 50);

        return deferred.promise;
    }

    /**
     * get's the record passed in through the canvas context
     */
    public record() {
        if (this.$rootScope.isSfdc) return this.canvasRequest().context.environment.record;
    }

    public canvasRequest() {
        if (this.$rootScope.isSfdc) return this.$rootScope.context.sfdcCanvasRequest;
    }

    /**
     * navigates to the salesforce view page of the object with the given id
     */
    public navigateToObject(id: string) {
        this.ensureReady()
            .then(() => {
                const sr = this.$rootScope.context.sfdcCanvasRequest;
                if (sr && window['Sfdc'] && window['Sfdc'].canvas && window['Sfdc'].canvas.client) {
                    Sfdc.canvas.client.publish(sr.client, {
                        name: "navigateToObject",
                        payload: id
                    });
                } else {
                    console.warn('Sfdc canvas not available for navigateToObject');
                }
            })
            .catch(err => console.warn('Sfdc not ready for navigateToObject', err));
    }

    public toggleFullscreen() {
        this.ensureReady()
            .then(() => {
                const sr = this.canvasRequest();
                if (sr && window['Sfdc'] && window['Sfdc'].canvas && window['Sfdc'].canvas.client) {
                    Sfdc.canvas.client.publish(sr.client, {
                        name: "toggleFullscreen",
                        payload: {}
                    });
                } else {
                    console.warn('Sfdc canvas not available for toggleFullscreen');
                }
            })
            .catch(err => console.warn('Sfdc not ready for toggleFullscreen', err));
    }

    /**
     * send a message to the visual force page hosting our canvas
     * @param msg
     */
    public sendMessage(msg: IMessage) {
        if (!this.$rootScope.isSfdc) return;
        this.ensureReady()
            .then(() => {
                const sr = this.canvasRequest();
                if (sr && window['Sfdc'] && window['Sfdc'].canvas && window['Sfdc'].canvas.client) {
                    Sfdc.canvas.client.publish(sr.client, {
                        name: msg.name,
                        payload: msg.data
                    });
                }
            })
            .catch(err => console.warn('Sfdc not ready for sendMessage', err));
    }

    /**
     * makes a quote the primary quote in sfdc by calling through the canvas 
     * @param quote
     */
    public makePrimaryQuote(quote: IQuote): ng.IPromise<any> {
        // Ensure SDK and context are available then call ajax
        return this.ensureReady().then(() => {
            let deferred = this.$q.defer();

            let sr = this.$rootScope.context.sfdcCanvasRequest;
            if (!sr) {
                deferred.reject(new Error('Sfdc canvas request not available'));
                return deferred.promise;
            }

            let url = sr.context.links.restUrl + "sobjects/KBMAX__Quote__c/" + quote.externalId;
            try {
                Sfdc.canvas.client.ajax(url, {
                    client: sr.client,
                    method: "PATCH",
                    contentType: "application/json",
                    data: JSON.stringify({
                        KBMAX__Primary__c: true
                    }),
                    success: data => {
                        if (data.status === 204) {
                            deferred.resolve();
                        } else {
                            deferred.reject();
                        }
                    },
                    error: (err) => deferred.reject(err)
                });
            } catch (e) {
                deferred.reject(e);
            }

            return deferred.promise;
        });
    }

    /**
     * when integrating with steelbrick, because of their easyXdm setup, we have to wait for the canvas
     * to tell us what the id of the product is.
     */
    public getPayloadFromSfdcCpq(): ng.IPromise<ISfdcCpqPayload> {
        // ensure SDK and context are ready
        return this.ensureReady().then(() => {
            let deferred = this.$q.defer<ISfdcCpqPayload>();
            let sr = this.$rootScope.context.sfdcCanvasRequest;

            if (!sr) {
                deferred.reject(new Error('Sfdc canvas request not available'));
                return deferred.promise;
            }

            Sfdc.canvas.client.subscribe(sr.client, {
                name: "sfdccpq_payload_response",
                onData: (e: ISfdcCpqPayload) => {
                    this.originalSfdcCpqPayload = this.cloneSfdcCpqPayload(e);
                    deferred.resolve(e);
                }
            });

            Sfdc.canvas.client.publish(sr.client, {
                name: "sfdccpq_payload_request",
                payload: {}
            });

            return deferred.promise;
        });
    }

    public getProducts(productCodes: string[]): ng.IPromise<ISfdcCpqGetProductsResponse_Product2[]> {
        return this.ensureReady().then(() => {
            let deferred = this.$q.defer<ISfdcCpqGetProductsResponse_Product2[]>();
            let sr = this.$rootScope.context.sfdcCanvasRequest;

            if (!sr) {
                deferred.reject(new Error('Sfdc canvas request not available'));
                return deferred.promise;
            }

            Sfdc.canvas.client.subscribe(sr.client, {
                name: "sfdccpq_getproducts_response",
                onData: (e: ISfdcCpqGetProductsResponse) => {
                    deferred.resolve(e.products);
                }
            });

            Sfdc.canvas.client.publish(sr.client, {
                name: "sfdccpq_getproducts",
                payload: { productCodes } as ISfdcCpqGetProductsRequest
            });

            return deferred.promise;
        });
    }

    private getColumnOrMetaValue(key: string, priceItem: IPriceItemBase) {
        // find the price column if there is one
        if (priceItem.columns) {
            let colNames = Object.keys(priceItem.columns);
            for (let colName of colNames) {
                if (colName.isEqual(key)) {
                    return priceItem.columns[colName];
                }
            }
        }
        // find the metadata property if there is one
        if (priceItem.metadata) {
            let metaNames = Object.keys(priceItem.metadata);
            for (let metaName of metaNames) {
                if (metaName.isEqual(key)) {
                    return priceItem.metadata[metaName];
                }
            }
        }

        return null;
    }

    protected getConfigurationAttribute(payload: ISfdcCpqPayload, name: string): string {
        for (let att in payload.data.product.configurationAttributes) {
            if (!att.isEqual("attributes") && att.isEqual(name)) {
                return att;
            }
        }
        return null;
    }

    protected addColumnsAndMetadataToConfigurationObject(
        payload: ISfdcCpqPayload,
        configurationData: {},
        item: IPriceItem
    ) {
        for (let col in item.columns) {
            let att = this.getConfigurationAttribute(payload, col);
            if (att) {
                configurationData[att] = item.columns[col];
            }
        }
        for (let m in item.metadata) {
            let att = this.getConfigurationAttribute(payload, m);
            if (att) {
                configurationData[att] = item.metadata[m];
            }
        }
    }

    private dynamicOptionsToQueryStatic: { item: IPriceItem, dynamicOption: ISfdcCpqPayloadOption }[] = [];
    protected buildOptions(payload: ISfdcCpqPayload, item: IPriceItem, optionConfig: { [name: string]: ISfdcCpqPayloadOption[]}, isDynamic: boolean = false) {
        if (!optionConfig) {
            let options: { [name: string]: ISfdcCpqPayloadOption[]};
            if (isDynamic)
                options = { 'Dynamic Options': [] };
            else
                options = { 'Other Options': []};
            optionConfig = options;
        }

        //let dynamicOptionsToQuery: { item: IPriceItem, dynamicOption: ISfdcCpqPayloadOption }[] = [];
        if (isDynamic) {
            
            item.items.forEach((element, index) => {
                let salesforceId = item.columns.salesforceId || item.metadata.salesforceId;
                let dynamicOption = {
                    ProductCode: element.sku,
                    Quantity: element.qty ?? 1,
                    configurationData: {},
                    productId: '',
                    selected: true
                }

                this.addColumnsAndMetadataToConfigurationObject(payload, dynamicOption.configurationData, element);
                // if (element.sku == 'y_product')
                //     dynamicOption.productId = '01t4x000008PckRAAS';
                // if (element.sku == 'z_product')
                //     dynamicOption.productId = '01t4x000008PcknAAC';
                optionConfig['Dynamic Options'][index] = dynamicOption;

                if (!salesforceId) {
                    this.dynamicOptionsToQueryStatic.push({ item: element, dynamicOption });
                }

                if (element.items.length) {
                    let dynOptsBuild = this.buildOptions(payload, element, optionConfig['Dynamic Options'][index].optionConfigurations, true);
                    optionConfig['Dynamic Options'][index].optionConfigurations = dynOptsBuild.load;
                    //this.dynamicOptionsToQueryStatic.push(...dynOptsBuild.dynOptionsToQuery);
                }

            })
        } else {
            item.items.forEach((element, index) => {
                optionConfig['Other Options'][index] = {
                    ProductCode: element.sku,
                    ProductName: Object.keys({element})[0],
                    optionId: element.externalId,
                    selected: true,
                    Quantity: element.qty ?? 1,
                    configurationData: {},
                };
                this.addColumnsAndMetadataToConfigurationObject(payload, optionConfig['Other Options'][index].configurationData, element);
                if (element.items.length > 0) {
                    optionConfig['Other Options'][index].optionConfigurations = this.buildOptions(payload, element, optionConfig['Other Options'][index].optionConfigurations).load;
                }
            });
        }
        
        
        return {load: optionConfig, dynOptionsToQuery: this.dynamicOptionsToQueryStatic};
    }
    protected clearNestedOptions(options: { [name: string]: ISfdcCpqPayloadOption[] }) {
        for (let option in options) {
            options[option].forEach(element => {
                element.selected = false;
                if (element.optionConfigurations) {
                    element.optionConfigurations = this.clearNestedOptions(element.optionConfigurations);
                }
            });
        }
        return options;
    }

    public saveToSfdcCpq(payload: ISfdcCpqPayload, priceObject: IPriceObject) {
        let sr = this.$rootScope.context.sfdcCanvasRequest;
        let payloadOrig = payload;
        // clear out old options
        for (let feature in payload.data.product.optionConfigurations) {
            if (feature.isEqual("Dynamic Options")) {
                payload.data.product.optionConfigurations[feature].forEach(option => {
                    option.Quantity = 0;
                    option.selected = false;
                }); // clear out dynamic options
            } else {
                payload.data.product.optionConfigurations[feature].forEach(option =>
                    option.selected = false); // unselect all pre-defined product options
                // unselect all pre-defined product options
                payload.data.product.optionConfigurations[feature].forEach(option => {
                    option.selected = false;
                    //delete option['optionConfigurations'];
                    //console.log(option.ProductCode, 'cleared');
                    if (option.optionConfigurations) {
                        option.optionConfigurations = this.clearNestedOptions(option.optionConfigurations);
                    }
                    //option.Quantity = 0;
                    
                });
            }
        }

        let dynamicOptionsToQuery: { item: IPriceItem, dynamicOption: ISfdcCpqPayloadOption }[] = [];

        if (priceObject) {
            // first we process non-dynamic product options
            // we recurse through all the price items and try to find matching product 
            // options that are not in the 'Dynamic Options' optionConfigurations object of the payload
            priceObject.items.forEach(item => {
                let foundInOptions = false;
                featureLoop:
                for (let feature in payload.data.product.optionConfigurations) {
                    if (!feature.isEqual("Dynamic Options")) {
                        let options: ISfdcCpqPayloadOption[] = payload.data.product.optionConfigurations[feature];
                        for (let option of options) {
                            if (item.sku == option.ProductCode) {
                                option.selected = true;
                                option.Quantity = item.qty ?? 1;
                                foundInOptions = true;

                                if (item.items.length > 0) {
                                    option.optionConfigurations = this.buildOptions(payload, item, option.optionConfigurations).load;
                                }
                                // load configuration attributes also
                                // we only add configuration attributes that are defined 
                                // in the configurationAttributes section of the json
                                option.configurationData = option.configurationData || {};
                                this.addColumnsAndMetadataToConfigurationObject(
                                    payload, option.configurationData, item);
                                
                                break featureLoop;
                            }
                        }
                    }
                }

                if (!foundInOptions) {
                    // if it's not found in the options, then we try to add it as a dynamic product option:  
                    // tslint:disable-next-line:max-line-length
                    // https://community.steelbrick.com/t5/Developer-Guidebook/Configurator-Integration-to-Third-Party-Web-Apps/ta-p/5575
                    let dynamicFeature = payload.data.product.optionConfigurations["Dynamic Options"];
                    // salesforceId must be defined for dynamic product options to work
                    let salesforceId = item.columns.salesforceId || item.metadata.salesforceId;
                    if (dynamicFeature) {
                        // first see if the dynamic product option is already there
                        //let dynamicOption: ISfdcCpqPayloadOption =
                        //    dynamicFeature.find(dynOpt => dynOpt.ProductCode && dynOpt.ProductCode.isEqual(item.sku));
                        //if (dynamicOption) {
                        //    dynamicOption.Quantity = item.qty;
                        //    dynamicOption.selected = true;
                        //} else {
                        let dynamicOption: ISfdcCpqPayloadOption = {
                            productId: salesforceId,
                            Quantity: item.qty,
                            configurationData: {},
                            selected: true,
                        };
                        

                        if (item.items.length) {
                            let dynOptsBuild = this.buildOptions(payload, item, dynamicOption.optionConfigurations, true);
                            dynamicOption.optionConfigurations = dynOptsBuild.load;
                            //dynamicOptionsToQuery.push(...dynOptsBuild.dynOptionsToQuery);
                        }
                        dynamicFeature.push(dynamicOption);

                        // if the salesforceId wasn't provided directly,
                        // then we make a call later to get the product id using the product code
                        if (!salesforceId) {
                            this.dynamicOptionsToQueryStatic.push({ item, dynamicOption });
                        }
                        this.addColumnsAndMetadataToConfigurationObject(
                            payload,
                            dynamicOption.configurationData,
                            item
                        );

                    }
                }
            });

            // set the product level configuration attributes that correspond to price columns or price item metadata
            if (payload.data.product.configurationAttributes) {
                for (let prop in payload.data.product.configurationAttributes) {
                    let attValue = this.getColumnOrMetaValue(prop, priceObject);
                    if (attValue != null) {
                        // priceObject corresponds to the top level product
                        payload.data.product.configurationAttributes[prop] = attValue;
                    }
                }
            }
        }

        let dynamicOptionsQueryPromise: ng.IPromise<any> = new KPromise(null) as any;
        if (this.dynamicOptionsToQueryStatic.length) {
            dynamicOptionsQueryPromise = this.getProducts(this.dynamicOptionsToQueryStatic.map(o => o.item.sku)
                .removeNulls())
                .then(products => {
                    this.dynamicOptionsToQueryStatic.forEach(dynOption => {
                        let product2 = products.find(p => p.ProductCode.isEqual(dynOption.item.sku));
                        if (!product2) {
                            // tslint:disable-next-line:max-line-length
                            let err = `Unable to add dynamic product option '${dynOption.item.sku}'.  No matching product record was found.`;
                            this.dialogService.alert({ msg: err, persist: true, type: eAlertType.error });
                            throw err;
                        } else {
                            dynOption.dynamicOption.productId = product2.Id;
                        }
                    });
                });
        }

        // DEBUG
        // if (Environment.isDebug) {
        // payload.data.quote["KBMAXSB__Test__c"] = "blah blah";
        // }
        // payload.data.product.optionConfigurations["DynamicFeature"].push(<any>{
        //    productId: "01t41000003vzS3",
        //    Quantity: 2,
        //    configurationData: {
        //        KBMAXSB__Test__c: "hey now",
        //        KBMAXSB__Number_Attribute__c: 444
        //    }
        // });

        // payload.data.product.optionConfigurations["DynamicFeature"].push(<any>{
        //    productId: "01t41000003vzS3",
        //    Quantity: 2,
        //    configurationData: {
        //        KBMAXSB__Test__c: "hey now",
        //        KBMAXSB__Number_Attribute__c: 444
        //    }
        // });

        dynamicOptionsQueryPromise.then(() => {
            // ensure SDK is ready and we have a canvas context before publishing
            this.ensureReady()
                .then(() => {
                    const ctx = this.$rootScope.context.sfdcCanvasRequest;
                    if (ctx && window['Sfdc'] && window['Sfdc'].canvas && window['Sfdc'].canvas.client) {
                        Sfdc.canvas.client.publish(ctx.client, {
                            name: "sfdccpq_save",
                            payload
                        });
                    } else {
                        console.warn('Sfdc canvas not available for sfdccpq_save');
                    }
                })
                .catch(err => console.warn('Sfdc not ready for sfdccpq_save', err));
        });

    }

    /**
     * Cancel Salesforce CPQ configurator edit and return to the line item list.
     * we have to just save the orifinal payload since the sfdc package doesn't have a cancel endpoint currently.
     */
    public cancelSfdcCpqEdit(payload?: ISfdcCpqPayload) {
        const originalPayload = this.cloneSfdcCpqPayload(payload || this.originalSfdcCpqPayload);
        if (!originalPayload) {
            console.warn('Original SFDC CPQ payload not available for cancel');
            return;
        }

        this.ensureReady()
            .then(() => {
                const ctx = this.$rootScope.context.sfdcCanvasRequest;
                if (ctx && window['Sfdc'] && window['Sfdc'].canvas && window['Sfdc'].canvas.client) {
                    Sfdc.canvas.client.publish(ctx.client, {
                        name: "sfdccpq_save",
                        payload: originalPayload
                    });
                } else {
                    console.warn('Sfdc canvas not available for sfdccpq_save (cancel path)');
                }
            })
            .catch(err => console.warn('Sfdc not ready for sfdccpq_save (cancel path)', err));
    }
}
